import { type App } from 'vue';
import { type NavigationGuardNext, type RouteLocationNormalized, useRouter } from 'vue-router';
import { useBitrixWidget } from '@/core/composables/bitrixWidget';
import { useErrorMessage } from '@/core/composables/useErrorMessage';
import { RouteNames } from '@/core/plugins/router/routeNames';
import { PermissionTarget, UserRegistrationStatus } from '@/graphql/generated';
import { useAuthStore } from '@/parts/auth/store/auth';

export const authMiddleware = async (
  to: RouteLocationNormalized,
  from: RouteLocationNormalized,
  next: NavigationGuardNext,
  app: App,
) => {
  const { $auth } = app.config.globalProperties;

  const { init: initBitrixWidget } = app.runWithContext(() => useBitrixWidget());

  const { setAuth, getProfile, registerKSBAccount } = app.runWithContext(() => useAuthStore());

  const toEmptyPage = (to: RouteLocationNormalized) => {
    if (to.name !== RouteNames.Empty) {
      next({ name: RouteNames.Empty });
    } else {
      next();
    }
  };

  // check if redirect after login
  if (to.name === RouteNames.Login) {
    // Inform the authentication service that a user logged in. Afterwards we send the user to the main page
    await $auth
      .handleLoginRedirect()
      .then(async () => {
        app.runWithContext(() => {
          const { afterLoginPath, removeAfterLoginPath } = useAuthStore();

          next(afterLoginPath());
          removeAfterLoginPath();
          return;
        });
        return;
      })
      .catch((error) => {
        app.runWithContext(() => {
          const { showErrorMessage } = useErrorMessage();
          const router = useRouter();

          const { stack, message, name } = error;

          if (message === 'No matching state found in storage') {
            router.push({ name: RouteNames.Home });
            return;
          }

          showErrorMessage({
            innerError: {
              name,
              message,
              stack,
            },
          });
        });

        next();

        console.error(error);
      });

    return;
  }

  // login if user is not authorized
  if (!(await $auth.isUserLoggedIn())) {
    $auth.login();
    next(false);
    return;
  } else {
    // set user is auth
    setAuth(true);
  }

  //get user data and redirect if no all data is filled
  const response = await getProfile();

  if (response?.errors) {
    if (response.errors[0].extensions?.code === 'AUTH_NOT_AUTHENTICATED') {
      $auth.logout();
      next(false);
      return;
    }

    toEmptyPage(to);

    return;
  }

  if (response.data?.me?.profile) {
    initBitrixWidget();
  }

  const user = response.data?.me;

  if (!user?.hasProfile) {
    if (to.name !== RouteNames.SignInProfile) {
      next({ name: RouteNames.SignInProfile });
    }
    next();
    return;
  }

  if (!user?.hasKsbAccount) {
    const result = await registerKSBAccount();

    if (!result || result.errors) {
      toEmptyPage(to);

      return;
    }

    await $auth.login();
    await getProfile();
  }

  if (to.name === RouteNames.Join) {
    next();
    return;
  }

  if (!user?.hasSupplier) {
    if (user?.userRegistration?.status === UserRegistrationStatus.Declined && to.name !== RouteNames.SignIn) {
      next({ name: RouteNames.SignIn });

      return;
    }

    if (user?.userRegistration?.status === UserRegistrationStatus.Declined) {
      next();
      return;
    }

    if (!user?.userRegistration && to.name !== RouteNames.SignIn && to.name !== RouteNames.SignInMaster) {
      next({ name: RouteNames.SignIn });

      return;
    }

    if (user?.userRegistration && to.name !== RouteNames.SignInMaster) {
      next({ name: RouteNames.SignInMaster });

      return;
    }

    next();
    return;
  } else {
    if (to.name === RouteNames.SignIn || to.name === RouteNames.SignInMaster) {
      next({ name: RouteNames.Home });

      return;
    }
  }

  //#region Permissions
  if (!user?.permissions.length && to.name !== RouteNames.RoleNotAssigned && to.meta.permissions?.length) {
    next({ name: RouteNames.RoleNotAssigned });
    return;
  }

  const redirectToAcceptedPage = () => {
    const homePagePriority = [
      {
        name: RouteNames.Home,
        permissions: [PermissionTarget.AccommodationObjects],
      },
      {
        name: RouteNames.Legals,
        permissions: [PermissionTarget.Legals],
      },
      {
        name: RouteNames.Documents,
        permissions: [PermissionTarget.Documents],
      },
      {
        name: RouteNames.Users,
        permissions: [PermissionTarget.Users],
      },
    ];

    for (const item of homePagePriority) {
      if (item.permissions.some((permissionsItem) => user?.permissions.includes(permissionsItem))) {
        next({ name: item.name });
        return;
      }
    }
  };

  if (to.name === RouteNames.RoleNotAssigned && user?.permissions.length) {
    redirectToAcceptedPage();
    return;
  }

  if (to.meta.permissions?.length && !to.meta.permissions.some((item) => user?.permissions.includes(item))) {
    if (to.name === RouteNames.Home && !from?.name) {
      redirectToAcceptedPage();
      return;
    }

    if (to.name !== RouteNames.AccessDenied) {
      next({ name: RouteNames.AccessDenied });
      return;
    }
  }
  //#endregion Permissions

  next();
};
